import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound, Users, X, Check } from "lucide-react";

export default function AdminBeta() {
  const t = useT();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [uses, setUses] = useState("10");

  const codes = useQuery({
    queryKey: ["admin-beta-codes"],
    queryFn: async () => (await api.get("/admin/beta/codes")).data,
    retry: false,
  });
  const requests = useQuery({
    queryKey: ["admin-beta-requests"],
    queryFn: async () => (await api.get("/admin/beta/requests")).data,
    retry: false,
  });
  const stats = useQuery({
    queryKey: ["admin-beta-stats"],
    queryFn: async () => (await api.get("/admin/beta/stats")).data,
    retry: false,
  });

  const codesList = Array.isArray(codes.data) ? codes.data : codes.data?.codes || [];
  const reqsList = Array.isArray(requests.data) ? requests.data : requests.data?.requests || [];

  const createCode = async () => {
    try {
      await api.post("/admin/beta/codes", { code: code.trim() || undefined, max_uses: parseInt(uses, 10) || 10 });
      setCode("");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-beta-codes"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revokeCode = async (c) => {
    try {
      await api.post(`/admin/beta/codes/${encodeURIComponent(c)}/revoke`);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-beta-codes"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const approveReq = async (email) => {
    try {
      await api.post(`/admin/beta/requests/${encodeURIComponent(email)}/approve`);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-beta-requests"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const declineReq = async (email) => {
    try {
      await api.post(`/admin/beta/requests/${encodeURIComponent(email)}/decline`);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-beta-requests"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.admin")} title={t("nav.beta")} subtitle={t("admin.subtitle")} />

      {stats.data && (
        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="beta-stats">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">Beta stats</div>
          <pre className="mt-2 text-xs text-ink-700 whitespace-pre-wrap font-mono">{JSON.stringify(stats.data, null, 2)}</pre>
        </div>
      )}

      <section className="rounded-3xl bg-white shadow-card p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-lg text-ink-900">{t("admin.createBetaCode")}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="OPTIONAL-CODE" className="rounded-xl bg-cream-50 h-11 max-w-xs font-mono" data-testid="beta-code-input" />
          <Input value={uses} onChange={(e) => setUses(e.target.value)} type="number" min="1" placeholder="max uses" className="rounded-xl bg-cream-50 h-11 w-32" data-testid="beta-code-uses" />
          <Button onClick={createCode} data-testid="beta-code-create" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">{t("admin.createBetaCode")}</Button>
        </div>

        {codes.isLoading && <LoadingCard height="h-24" />}
        {codes.isError && <ErrorState message={errMsg(codes.error)} onRetry={() => codes.refetch()} />}
        {codesList.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="beta-codes-list">
            {codesList.map((c, i) => (
              <div key={c.code || c.id || i} className="flex items-center justify-between rounded-2xl border border-border p-3 bg-cream-50">
                <div className="font-mono text-ink-900">{c.code || c.id}</div>
                <div className="text-xs text-ink-500">
                  {c.uses ?? 0}/{c.max_uses ?? "∞"} · {c.status || "active"}
                </div>
                <Button size="sm" variant="outline" className="rounded-full border-border text-rust-600 h-8" onClick={() => revokeCode(c.code || c.id)} data-testid={`beta-code-revoke-${i}`}>
                  <X className="h-3.5 w-3.5 mr-1" /> {t("common.revoke")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-lg text-ink-900">Access requests</h2>
        </div>
        {requests.isLoading && <LoadingCard />}
        {requests.isError && <ErrorState message={errMsg(requests.error)} onRetry={() => requests.refetch()} />}
        {!requests.isLoading && !requests.isError && reqsList.length === 0 && (
          <EmptyState icon={Users} title={t("common.empty")} testId="beta-requests-empty" />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="beta-requests">
          {reqsList.map((r, i) => (
            <div key={r.email || i} className="rounded-2xl bg-white shadow-card p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-ink-900 font-medium truncate">{r.name || r.email}</div>
                <div className="text-xs text-ink-500 truncate">{r.email}</div>
                {r.organisation && <div className="text-xs text-ink-500 mt-1">{r.organisation}</div>}
                {r.reason && <div className="text-sm text-ink-700 mt-2 line-clamp-3">{r.reason}</div>}
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="rounded-full border-border h-8" data-testid={`beta-req-approve-${i}`} onClick={() => approveReq(r.email)}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" className="rounded-full border-border h-8 text-rust-600" data-testid={`beta-req-decline-${i}`} onClick={() => declineReq(r.email)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
